import FilterController from "../sharedControllers/FilterController";
import { MonthData, TimelineRow } from "../../models/TimelineRow";
import NumberFormat from "sap/ui/core/format/NumberFormat";
import { TimelineItem } from "../../models/TimelineItem";
import JSONModel from "sap/ui/model/json/JSONModel";
import { Chart, ChartDataset } from "chart.js/auto";
import Link, { Link$PressEvent } from "sap/m/Link";
import ColumnListItem from "sap/m/ColumnListItem";
import { Routes } from "../../constants/Routes";
import CustomData from "sap/ui/core/CustomData";
import ObjectNumber from "sap/m/ObjectNumber";
import { PlacementType } from "sap/m/library";
import Control from "sap/ui/core/Control";
import MessageBox from "sap/m/MessageBox";
import Event from "sap/ui/base/Event";
import Popover from "sap/m/Popover";
import HTML from "sap/ui/core/HTML";
import Dialog from "sap/m/Dialog";
import Column from "sap/m/Column";
import Table from "sap/m/Table";
import HBox from "sap/m/HBox";
import VBox from "sap/m/VBox";
import Text from "sap/m/Text";

// =============================================================================
// Types & constants
// =============================================================================

type SettingsType = "TIMELINE" | "ITEMS";
type TimelinePeriod = "weekly" | "monthly" | "quarterly" | "yearly";
type TimelineAxis = "X" | "Y";
/** Which axis select the user just changed (passed from the view). */
type ChangedAxis = "x" | "y";
type FixateSlot = 1 | 2 | 3;
type ChartKind = "line" | "bar";

/**
 * Describes how dates are bucketed into periods (week / month / quarter / year).
 * Every period is mapped to an integer ordinal; consecutive periods always differ by 1,
 * which makes sorting and gap filling a simple integer range.
 */
interface PeriodDefinition {
    /** Ordinal of the period containing the given (local) date. */
    toOrdinal(oDate: Date): number;
    /** Stable key used as binding path / sub-item id. */
    key(iOrdinal: number): string;
    /** Human-readable column / axis label. */
    label(iOrdinal: number): string;
}

interface GroupedTimeline {
    rows: TimelineRow[];
    /** Column keys. On the time axis they are sorted and continuous (gaps are filled). */
    periodKeys: string[];
    periodLabels: Record<string, string>;
}

interface PeriodOption {
    id: TimelinePeriod;
    enabled?: boolean;
}

interface Fixate {
    value: string;
    dimension: string;
}

const MS_PER_DAY = 86_400_000;
const MS_PER_WEEK = 7 * MS_PER_DAY;
/** A Monday (1969-12-29, UTC) used as the origin for week ordinals. */
const WEEK_EPOCH = Date.UTC(1969, 11, 29);

/** X item meaning "dates bucketed by the selected period". */
const TIME_AXIS = "Time";
/** Y value of the summary row appended to every grouping. */
const TOTAL_ROW = "Total";
/** Calc option that uses work-order totals; every other option uses contract totals. */
const CALC_CONTRACT_POSITIONS = "ContractPositions";

const FIXATE_SLOTS: FixateSlot[] = [1, 2, 3];

/** Default number of period columns shown at once in the timeline table / chart. */
const DEFAULT_WINDOW_SIZE = 12;

/** Dimensions the user can drill into from a row of the timeline table. */
const TIMELINE_DIMENSIONS = ["BusinessUnit", "Partner", "Author", "Service"] as const;

/** Period used when the selected one is not available for the selected date range. */
const FALLBACK_PERIOD: TimelinePeriod = "weekly";

/**
 * Minimum length (in days, both ends inclusive) of the selected date range for a period to be selectable.
 * The values are the shortest possible month (Feb), quarter (Q1 in a non-leap year) and year.
 */
const MIN_RANGE_DAYS: Record<TimelinePeriod, number> = {
    weekly: 0,
    monthly: 28,
    quarterly: 90,
    yearly: 365,
};

const CHART_PALETTE = [
    "#5B8FF9", "#61DDAA", "#F6BD16", "#7262FD", "#78D3F8",
    "#9661BC", "#F6903D", "#008685", "#F08BB4", "#E8684A",
];

// =============================================================================
// Helpers
// =============================================================================

/** Converts any value to a number, falling back to 0 for NaN / Infinity / garbage. */
function toFiniteNumber(vValue: unknown): number {
    const nValue = Number(vValue);
    return Number.isFinite(nValue) ? nValue : 0;
}

/** Formats a date as yyyy-MM-dd in local time. */
function toIsoDate(oDate: Date): string {
    return [
        oDate.getFullYear(),
        String(oDate.getMonth() + 1).padStart(2, "0"),
        String(oDate.getDate()).padStart(2, "0"),
    ].join("-");
}

/**
 * Parses "yyyy-MM-dd" (optionally followed by a time part) as a LOCAL calendar date.
 * `new Date("yyyy-MM-dd")` would parse as UTC midnight, which shifts the date by one day
 * for users west of UTC.
 */
function parseLocalDate(vValue: string | Date): Date {
    if (vValue instanceof Date) {
        return vValue;
    }
    const aMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(vValue);
    return aMatch ? new Date(+aMatch[1], +aMatch[2] - 1, +aMatch[3]) : new Date(vValue);
}

/** First and last day of the previous calendar month. */
function getPreviousMonthRange(oToday: Date = new Date()): { from: Date; to: Date } {
    return {
        from: new Date(oToday.getFullYear(), oToday.getMonth() - 1, 1),
        to: new Date(oToday.getFullYear(), oToday.getMonth(), 0),
    };
}

/** Number of calendar days from oFrom to oTo, both included. DST-safe (compares UTC calendar dates). */
function countDaysInclusive(oFrom: Date, oTo: Date): number {
    const iFrom = Date.UTC(oFrom.getFullYear(), oFrom.getMonth(), oFrom.getDate());
    const iTo = Date.UTC(oTo.getFullYear(), oTo.getMonth(), oTo.getDate());
    return Math.abs(iTo - iFrom) / MS_PER_DAY + 1;
}

/** ISO 8601 week: weeks start on Monday, week 1 contains the year's first Thursday. */
function getIsoWeek(oDate: Date): { year: number; week: number } {
    const oUtc = new Date(Date.UTC(oDate.getFullYear(), oDate.getMonth(), oDate.getDate()));
    const iDay = oUtc.getUTCDay() || 7;                 // Mon = 1 … Sun = 7
    oUtc.setUTCDate(oUtc.getUTCDate() + 4 - iDay);      // Thursday of the same week decides the year
    const iYear = oUtc.getUTCFullYear();
    const iWeek = Math.ceil(((oUtc.getTime() - Date.UTC(iYear, 0, 1)) / MS_PER_DAY + 1) / 7);
    return { year: iYear, week: iWeek };
}

/** Monday (as a local date) of the week with the given ordinal. */
function weekStart(iOrdinal: number): Date {
    const oUtc = new Date(WEEK_EPOCH + iOrdinal * MS_PER_WEEK);
    return new Date(oUtc.getUTCFullYear(), oUtc.getUTCMonth(), oUtc.getUTCDate());
}

/** Builds a definition for periods that divide a calendar year evenly (month, quarter, year). */
function calendarPeriod(
    iPerYear: number,
    indexOf: (iMonth: number) => number,
    key: (iYear: number, iIndex: number) => string,
    label: (iYear: number, iIndex: number) => string,
): PeriodDefinition {
    const split = (iOrdinal: number): [number, number] =>
        [Math.floor(iOrdinal / iPerYear), (iOrdinal % iPerYear) + 1];

    return {
        toOrdinal: (oDate) => oDate.getFullYear() * iPerYear + indexOf(oDate.getMonth() + 1) - 1,
        key: (iOrdinal) => key(...split(iOrdinal)),
        label: (iOrdinal) => label(...split(iOrdinal)),
    };
}

const PERIOD_DEFINITIONS: Record<TimelinePeriod, PeriodDefinition> = {
    // Weeks don't divide a year evenly (52 or 53 ISO weeks, crossing year boundaries),
    // so they are counted continuously from a fixed Monday instead.
    weekly: {
        toOrdinal: (oDate) => {
            const iUtc = Date.UTC(oDate.getFullYear(), oDate.getMonth(), oDate.getDate());
            return Math.floor((iUtc - WEEK_EPOCH) / MS_PER_WEEK);
        },
        key: (iOrdinal) => {
            const { year, week } = getIsoWeek(weekStart(iOrdinal));
            return `${year}-W${String(week).padStart(2, "0")}`;
        },
        label: (iOrdinal) => {
            const { year, week } = getIsoWeek(weekStart(iOrdinal));
            return `W${week} ${year}`;
        },
    },
    monthly: calendarPeriod(
        12,
        (iMonth) => iMonth,
        (iYear, iMonth) => `${iYear}-${String(iMonth).padStart(2, "0")}`,
        (iYear, iMonth) =>
            new Date(iYear, iMonth - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" }),
    ),
    quarterly: calendarPeriod(
        4,
        (iMonth) => Math.ceil(iMonth / 3),
        (iYear, iQuarter) => `${iYear}-${iQuarter}`,
        (iYear, iQuarter) => `Q${iQuarter} ${iYear}`,
    ),
    yearly: calendarPeriod(
        1,
        () => 1,
        (iYear) => String(iYear),
        (iYear) => String(iYear),
    ),
};

function getSeriesColor(iIndex: number): string {
    if (iIndex < CHART_PALETTE.length) {
        return CHART_PALETTE[iIndex];
    }
    const nHue = (iIndex * 137.508) % 360; // golden angle, keeps extra series visually distinct
    return `hsl(${nHue}, 65%, 55%)`;
}

/** Adds price / total to the cell of a row, creating the cell when missing. */
function addToCell(oRow: TimelineRow, sKey: string, fPrice: number, fTotal: number): void {
    const oCell = (oRow[sKey] as MonthData | undefined) ?? { price: 0, total: 0 };
    oRow[sKey] = {
        price: oCell.price + fPrice,
        total: oCell.total + fTotal,
    };
}

/** Adds the key to the set; returns true only the first time a key is seen. */
function addOnce(oSeen: Set<string>, sKey: string): boolean {
    if (oSeen.has(sKey)) {
        return false;
    }
    oSeen.add(sKey);
    return true;
}

/**
 * @namespace akademika.akamont.pages.analytics
 */
export default class Analytics extends FilterController {
    route = Routes.ANALYTICS;
    parentRoute = Routes.DASHBOARD;

    _analyticsItemsListUri = "api/Analytics/GetAnalyticsItemsList";
    _analyticsItemsListOptions = "api/Analytics/GetItemsListOptions";
    _analyticsTimelineOptions = "api/Analytics/GetTimelineOptions";
    _analyticsTimelineData = "api/Analytics/GetTimelineData";
    _analyticsSaveTimelineSettings = "api/Analytics/SaveTimelineSettings";
    _analyticsGetTimelineSettings = "api/Analytics/GetTimelineSettings";
    _analyticsDeleteTimelineSettings = "api/Analytics/DeleteTimelineSettings";
    _analyticsSaveTemplateSettings = "api/Analytics/SaveTemplateSettings";
    _analyticsSaveItemsSettings = "api/Analytics/SaveItemsSettings";
    _analyticsSettingsList = "api/Analytics/GetSettingsList";

    private readonly _oAmountFormat = NumberFormat.getFloatInstance({ minFractionDigits: 2, maxFractionDigits: 2 });
    private readonly _oPercentFormat = NumberFormat.getPercentInstance({ maxFractionDigits: 1 });

    private _oTimelineChart?: Chart<ChartKind>;
    private _oFixatePopover?: Popover;
    /** Last grouped data, so moving the column window re-renders without a backend call. */
    private _oLastGrouped?: GroupedTimeline;
    private _bTimelineInitialized = false;
    /** Incremented per data request; responses of outdated requests are ignored. */
    private _iTimelineRequestId = 0;
    private _oPopover?: Popover;
    protected _settingsDialog: Dialog | null;
    /** Which view the settings dialog was opened from. */
    private _currentSettingsType: SettingsType = "TIMELINE";

    // =========================================================================
    // Lifecycle
    // =========================================================================

    onInit() {
        super.onInit();

        this.initFilters(this.getDefaultFilters(), this._analyticsItemsListUri);

        this.fetchModel(this._analyticsItemsListOptions, "filterOptions");
    }

    /** Empty / initial state of the items list filters (also used when loading saved filters). */
    private getDefaultFilters() {
        const oToday = new Date();
        return {
            businessUnit: undefined,
            partner: undefined,
            author: undefined,
            contractNumber: undefined,
            timePeriodFrom: toIsoDate(new Date(oToday.getFullYear(), oToday.getMonth() - 1, 1)),
            timePeriodTo: toIsoDate(new Date(oToday.getFullYear(), oToday.getMonth(), 0)),
            amount: { from: undefined as number | undefined, to: undefined as number | undefined },
        };
    }

    public onTabSelect(oEvent: Event): void {
        if (oEvent.getParameter("key" as never) !== "timeline") return;

        if (!this._bTimelineInitialized) {
            this._bTimelineInitialized = true;
            void this.initializeTimeline();
        } else {
            // Keep the user's configuration; just redraw in case the tab content was re-rendered.
            this.rerenderFromCache();
        }
    }

    public onExit() {
        this._oTimelineChart?.destroy();
        this._oFixatePopover?.destroy();
    }

    // =========================================================================
    // Timeline: configuration & data loading
    // =========================================================================

    public async initializeTimeline(): Promise<void> {
        const { from, to } = getPreviousMonthRange();

        this.setModel(new JSONModel({
            timelineXItem: TIME_AXIS,
            timelineYItem: "BusinessUnit",
            timelineYValue1: "",
            timelineXFixate1: "",
            timelineYFixate1: "",
            timelineYValue2: "",
            timelineXFixate2: "",
            timelineYFixate2: "",
            timelineYValue3: "",
            timelineXFixate3: "",
            timelineYFixate3: "",
            timePeriodFrom: from,
            timePeriodTo: to,
            timelineCalcOption: CALC_CONTRACT_POSITIONS,
            timelinePeriod: FALLBACK_PERIOD,
            // Column window (paging through periods)
            timelineWindowSize: DEFAULT_WINDOW_SIZE,
            timelineWindowStart: -1, // -1 = show the most recent periods
            timelineWindowText: "",
            timelineCanPrev: false,
            timelineCanNext: false,
            timelineWindowVisible: false,
            newSettingName: ""
        }), "timeline");

        await this.fetchModel(this._analyticsTimelineOptions, "timelineOptions");
        await this.applyTimelineConfiguration();
    }

    /**
     * Reloads timeline data from the backend and re-renders table + chart.
     * @param resetSubItems true for a new configuration (resets sub-item selection and column window),
     *                      false for drill-downs (keeps the user's position; it is clamped on render)
     * @param changedAxis   the axis select the user just changed, used to resolve X/Y conflicts
     */
    public async applyTimelineConfiguration(resetSubItems = true, changedAxis?: ChangedAxis): Promise<void> {
        if (resetSubItems) {
            this.resetSubItems();
            this.resetColumnWindow();
        }

        this.resolveAxisConflict(changedAxis);
        this.updatePeriodAvailability();

        const iRequestId = ++this._iTimelineRequestId;
        await this.fetchModelPost(this._analyticsTimelineData, "timelineData", {
            body: this.buildTimelineRequest(),
        });
        if (iRequestId !== this._iTimelineRequestId) {
            return; // a newer configuration was applied while this request was running
        }

        const oGrouped = this.groupTimelineData(this.getTimelineContent());
        if (resetSubItems) {
            this.syncSubItemOptions(oGrouped);
        }
        this.renderTimeline(oGrouped);
    }

    public resetSubItems(): void {
        const oTimelineModel = this.getModel("timeline");
        oTimelineModel.setProperty("/timelineXSubItems", null);
        oTimelineModel.setProperty("/timelineYSubItems", null);
    }

    /**
     * X and Y must not show the same dimension. The axis the user just changed wins;
     * the other axis is moved to a different dimension.
     */
    private resolveAxisConflict(sChangedAxis?: ChangedAxis): void {
        const oTimelineModel = this.getModel("timeline");
        const sX = oTimelineModel.getProperty("/timelineXItem");
        const sY = oTimelineModel.getProperty("/timelineYItem");

        if (sX !== sY) {
            return;
        }

        if (sChangedAxis === "x") {
            oTimelineModel.setProperty("/timelineYItem", sX === "BusinessUnit" ? "Partner" : "BusinessUnit");
        } else {
            // Time is never a Y option, so it is always a safe fallback for X.
            oTimelineModel.setProperty("/timelineXItem", TIME_AXIS);
        }
    }

    /**
     * Disables periods that are longer than the selected date range
     * and falls back to weekly if the current period got disabled.
     */
    private updatePeriodAvailability(): void {
        const oTimelineModel = this.getModel("timeline");
        const oOptionsModel = this.getModel("timelineOptions");
        const aPeriods = (oOptionsModel.getProperty("/timelinePeriods") ?? []) as PeriodOption[];
        const iRangeDays = this.getSelectedRangeInDays();

        aPeriods.forEach((oPeriod, iIndex) => {
            const bEnabled = iRangeDays >= (MIN_RANGE_DAYS[oPeriod.id] ?? 0);
            oOptionsModel.setProperty(`/timelinePeriods/${iIndex}/enabled`, bEnabled);

            if (!bEnabled && oTimelineModel.getProperty("/timelinePeriod") === oPeriod.id) {
                oTimelineModel.setProperty("/timelinePeriod", FALLBACK_PERIOD);
            }
        });
    }

    private buildTimelineRequest(): Record<string, unknown> {
        const oTimelineModel = this.getModel("timeline");
        const get = (sProperty: string) => oTimelineModel.getProperty("/" + sProperty);

        return {
            x: get("timelineXItem"),
            y: get("timelineYItem"),
            yV1: get("timelineYValue1"),
            yF1: get("timelineYFixate1"),
            yV2: get("timelineYValue2"),
            yF2: get("timelineYFixate2"),
            yV3: get("timelineYValue3"),
            yF3: get("timelineYFixate3"),
            from: toIsoDate(get("timePeriodFrom")),
            to: toIsoDate(get("timePeriodTo")),
            calcOption: get("timelineCalcOption"),
            xSubItems: get("timelineXSubItems"),
            ySubItems: get("timelineYSubItems"),
        };
    }

    private getTimelineContent(): TimelineItem[] {
        return this.getModel("timelineData").getData() || [];
    }

    /** Fills the sub-item selection lists from the loaded data (only while nothing is fixated on that axis). */
    private syncSubItemOptions({ rows, periodKeys, periodLabels }: GroupedTimeline): void {
        const oTimelineModel = this.getModel("timeline");
        const oOptionsModel = this.getModel("timelineOptions");

        if (!this.hasAnyFixate("X")) {
            oOptionsModel.setProperty("/timelineXSubItems", periodKeys.map((sKey) => ({ id: sKey, name: periodLabels[sKey] })));
            if (oTimelineModel.getProperty("/timelineXSubItems") == null) {
                oTimelineModel.setProperty("/timelineXSubItems", [...periodKeys]);
            }
        }

        if (!this.hasAnyFixate("Y")) {
            oOptionsModel.setProperty("/timelineYSubItems", rows.map((oRow) => ({ id: oRow.y, name: oRow.y })));
            if (oTimelineModel.getProperty("/timelineYSubItems") == null) {
                oTimelineModel.setProperty("/timelineYSubItems", rows.map((oRow) => oRow.y));
            }
        }
    }

    /** Restricts rows / periods to the sub-items the user selected. */
    private applySubItemSelection(oGrouped: GroupedTimeline): GroupedTimeline {
        const oTimelineModel = this.getModel("timeline");
        const aSelectedX: string[] | null = oTimelineModel.getProperty("/timelineXSubItems");
        const aSelectedY: string[] | null = oTimelineModel.getProperty("/timelineYSubItems");

        const bFilterX = aSelectedX != null && !this.hasAnyFixate("X");
        const bFilterY = aSelectedY != null && !this.hasAnyFixate("Y");

        return {
            rows: bFilterY ? oGrouped.rows.filter((oRow) => aSelectedY?.includes(oRow.y)) : oGrouped.rows,
            periodKeys: bFilterX ? oGrouped.periodKeys.filter((sKey) => aSelectedX?.includes(sKey)) : oGrouped.periodKeys,
            periodLabels: oGrouped.periodLabels,
        };
    }

    // =========================================================================
    // Timeline: date range & periods
    // =========================================================================

    private isTimeAxis(): boolean {
        return this.getModel("timeline").getProperty("/timelineXItem") === TIME_AXIS;
    }

    private getPeriodDefinition(): PeriodDefinition {
        const sPeriod = this.getModel("timeline").getProperty("/timelinePeriod") as TimelinePeriod;
        return PERIOD_DEFINITIONS[sPeriod] || PERIOD_DEFINITIONS.monthly;
    }

    /** Selected [from, to] dates in ascending order, or null when not set or invalid. */
    private getSelectedDateRange(): [Date, Date] | null {
        const oTimelineModel = this.getModel("timeline");
        const vFrom = oTimelineModel.getProperty("/timePeriodFrom") as Date | string | null | undefined;
        const vTo = oTimelineModel.getProperty("/timePeriodTo") as Date | string | null | undefined;

        if (!vFrom || !vTo) {
            return null;
        }

        const oFrom = parseLocalDate(vFrom);
        const oTo = parseLocalDate(vTo);
        if (Number.isNaN(oFrom.getTime()) || Number.isNaN(oTo.getTime())) {
            return null;
        }

        return oFrom.getTime() <= oTo.getTime() ? [oFrom, oTo] : [oTo, oFrom];
    }

    /** Ordinal range [first, last] of the selected date range, or null when the range is not set. */
    private getSelectedPeriodRange(oPeriod: PeriodDefinition): [number, number] | null {
        const aRange = this.getSelectedDateRange();
        return aRange ? [oPeriod.toOrdinal(aRange[0]), oPeriod.toOrdinal(aRange[1])] : null;
    }

    /** Length of the selected date range in days (both ends included); 0 when not set. */
    private getSelectedRangeInDays(): number {
        const aRange = this.getSelectedDateRange();
        return aRange ? countDaysInclusive(aRange[0], aRange[1]) : 0;
    }

    // =========================================================================
    // Timeline: grouping
    // =========================================================================

    /**
     * Groups raw items into rows (one per Y value) plus a "Total" row.
     * - Time on X: items are bucketed by the selected period and the columns cover the whole
     *   selected date range, including periods without data. If the backend returns data
     *   outside that range, the range is extended to include it.
     * - Any other X: columns are the distinct X values in order of appearance.
     *
     * Each cell holds the summed price and the total it is compared against (work-order total
     * or contract total, depending on the calc option). A contract's total is counted only once
     * per cell, no matter how many items of that contract fall into the cell.
     */
    private groupTimelineData(aContent: TimelineItem[]): GroupedTimeline {
        const bTimeAxis = this.isTimeAxis();
        const oPeriod = this.getPeriodDefinition();
        const bUseWorkOrderTotal = this.getModel("timeline").getProperty("/timelineCalcOption") === CALC_CONTRACT_POSITIONS;

        const mRows = new Map<string, TimelineRow>();
        const oTotalRow = { y: TOTAL_ROW } as TimelineRow;
        const aColumnKeys: string[] = [];
        const oColumnLabels: Record<string, string> = {};

        const oCountedInRow = new Set<string>();   // column | row | contract
        const oCountedInTotal = new Set<string>(); // column | contract

        let iFirst = Infinity;
        let iLast = -Infinity;

        for (const oItem of aContent) {
            let sColumnKey: string;

            if (bTimeAxis) {
                const iOrdinal = oPeriod.toOrdinal(parseLocalDate(oItem.x));
                iFirst = Math.min(iFirst, iOrdinal);
                iLast = Math.max(iLast, iOrdinal);
                sColumnKey = oPeriod.key(iOrdinal);
            } else {
                sColumnKey = oItem.x;
                if (!(sColumnKey in oColumnLabels)) {
                    aColumnKeys.push(sColumnKey);
                    oColumnLabels[sColumnKey] = sColumnKey;
                }
            }

            let oRow = mRows.get(oItem.y);
            if (!oRow) {
                oRow = { y: oItem.y } as TimelineRow;
                mRows.set(oItem.y, oRow);
            }

            const fPrice = toFiniteNumber(oItem.price);
            const fTotal = toFiniteNumber(bUseWorkOrderTotal ? oItem.workOrderTotal : oItem.contractTotal);

            const bFirstInRowCell = addOnce(oCountedInRow, bUseWorkOrderTotal ? `${sColumnKey}|${oItem.y}|${oItem.workOrder}` : `${sColumnKey}|${oItem.y}|${oItem.contract}`);
            const bFirstInTotalCell = addOnce(oCountedInTotal, bUseWorkOrderTotal ? `${sColumnKey}|${oItem.workOrder}` : `${sColumnKey}|${oItem.contract}`);

            addToCell(oRow, sColumnKey, fPrice, bFirstInRowCell ? fTotal : 0);
            addToCell(oTotalRow, sColumnKey, fPrice, bFirstInTotalCell ? fTotal : 0);
        }

        if (bTimeAxis) {
            // Always show the whole selected range, not just the periods that have data
            const aRange = this.getSelectedPeriodRange(oPeriod);
            if (aRange) {
                iFirst = Math.min(iFirst, aRange[0]);
                iLast = Math.max(iLast, aRange[1]);
            }

            // Continuous timeline from first to last period (empty when there is neither data nor a range)
            for (let iOrdinal = iFirst; iOrdinal <= iLast; iOrdinal++) {
                const sKey = oPeriod.key(iOrdinal);
                aColumnKeys.push(sKey);
                oColumnLabels[sKey] = oPeriod.label(iOrdinal);
            }
        }

        mRows.set(TOTAL_ROW, oTotalRow);

        return { rows: [...mRows.values()], periodKeys: aColumnKeys, periodLabels: oColumnLabels };
    }

    // =========================================================================
    // Timeline: rendering
    // =========================================================================

    private renderTimeline(oGrouped: GroupedTimeline): void {
        this._oLastGrouped = oGrouped;

        const { rows, periodKeys, periodLabels } = this.applySubItemSelection(oGrouped);
        const aVisibleKeys = this.applyColumnWindow(periodKeys, periodLabels);

        this.setModel(new JSONModel({ rows }), "timelineTable");
        this.renderTimelineTable(aVisibleKeys, periodLabels);
        this.renderTimelineChart(rows, aVisibleKeys, periodLabels);
    }

    private rerenderFromCache(): void {
        if (this._oLastGrouped) {
            this.renderTimeline(this._oLastGrouped);
        }
    }

    private renderTimelineTable(aPeriodKeys: string[], oPeriodLabels: Record<string, string>): void {
        const oTable = this.byId("timelineTable") as Table;
        oTable.addStyleClass("timelineTable"); // scopes the Total row CSS to this table

        oTable.destroyColumns();
        oTable.addColumn(new Column({ header: new Text({ text: "" }) }));
        aPeriodKeys.forEach((sKey) => {
            oTable.addColumn(new Column({ header: new Text({ text: oPeriodLabels[sKey] }), hAlign: "End" }));
        });

        const aCells: Control[] = [
            this.createRowHeaderCell(),
            ...aPeriodKeys.map((sKey) => this.createAmountCell(sKey)),
        ];

        oTable.bindItems({
            path: "timelineTable>/rows",
            template: this.createRowTemplate(aCells),
        });
    }

    /**
     * Row template. Each row gets data-rowtype="total" or "regular" written to its DOM element,
     * so the Total row can be styled in CSS (see .timelineTable rules in style.css).
     */
    private createRowTemplate(aCells: Control[]): ColumnListItem {
        return new ColumnListItem({
            cells: aCells,
            customData: [
                new CustomData({
                    key: "rowtype",
                    value: `{= \${timelineTable>y} === '${TOTAL_ROW}' ? 'total' : 'regular' }`,
                    writeToDom: true,
                }),
            ],
        });
    }

    /** Row label; a drill-down link unless all fixate slots are used or the row is "Total". */
    private createRowHeaderCell(): Control {
        if (this.areAllFixateSlotsUsed()) {
            return new Text({ text: "{timelineTable>y}" });
        }
        return new HBox({
            items: [
                new Text({
                    text: "{timelineTable>y}",
                    visible: `{= \${timelineTable>y} === '${TOTAL_ROW}' }`,
                }),
                new Link({
                    text: "{timelineTable>y}",
                    visible: `{= \${timelineTable>y} !== '${TOTAL_ROW}' }`,
                    press: (oEvent) => this.onTimelineParameterPress(oEvent),
                }),
            ],
        });
    }

    /** Amount for one period, with its share of the total below it. */
    private createAmountCell(sPeriodKey: string): Control {
        const sPricePath = `timelineTable>${sPeriodKey}/price`;
        const sTotalPath = `timelineTable>${sPeriodKey}/total`;

        return new VBox({
            items: [
                new ObjectNumber({
                    number: {
                        path: sPricePath,
                        formatter: (fPrice?: number | null): string => this._oAmountFormat.format(fPrice ?? 0),
                    },
                }),
                new Text({
                    text: {
                        parts: [{ path: sPricePath }, { path: sTotalPath }],
                        formatter: (fPrice?: number | null, fTotal?: number | null): string => this.formatShare(fPrice, fTotal),
                    },
                }).addStyleClass("percentageText"),
            ],
            alignItems: "End",
        });
    }

    private formatShare(fPrice?: number | null, fTotal?: number | null): string {
        const fSafeTotal = fTotal ?? 0;
        return this._oPercentFormat.format(fSafeTotal ? (fPrice ?? 0) / fSafeTotal : 0);
    }

    private renderTimelineChart(aRows: TimelineRow[], aPeriodKeys: string[], oPeriodLabels: Record<string, string>): void {
        const oCanvas = this.getChartCanvas();
        if (!oCanvas) {
            return;
        }

        oCanvas.height = 400;

        const aDatasets: ChartDataset<ChartKind>[] = aRows.map((oRow, iIndex) => {
            const sColor = getSeriesColor(iIndex);
            return {
                label: oRow.y,
                data: aPeriodKeys.map((sKey) => toFiniteNumber((oRow[sKey] as MonthData | undefined)?.price)),
                backgroundColor: sColor,
                borderColor: sColor,
            };
        });

        this._oTimelineChart?.destroy();
        this._oTimelineChart = new Chart<ChartKind>(oCanvas, {
            type: this.isTimeAxis() ? "line" : "bar",
            data: {
                labels: aPeriodKeys.map((sKey) => oPeriodLabels[sKey]),
                datasets: aDatasets,
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },
                scales: { y: { beginAtZero: true } },
            },
        });
    }

    private getChartCanvas(): HTMLCanvasElement | null {
        return ((this.byId("timelineChartContainer") as HTML)
            ?.getDomRef()
            ?.querySelector("canvas") as HTMLCanvasElement | null) ?? null;
    }

    // =========================================================================
    // Timeline: column window (paging through periods)
    // =========================================================================

    public resetColumnWindow(): void {
        this.getModel("timeline").setProperty("/timelineWindowStart", -1);
    }

    private getWindowSize(): number {
        const iSize = Math.floor(toFiniteNumber(this.getModel("timeline").getProperty("/timelineWindowSize")));
        return iSize > 0 ? iSize : DEFAULT_WINDOW_SIZE;
    }

    /**
     * Returns the slice of period keys to display and updates the navigation state
     * (button enablement, range text, visibility of the navigation bar).
     */
    private applyColumnWindow(aKeys: string[], oLabels: Record<string, string>): string[] {
        const oTimelineModel = this.getModel("timeline");
        const iSize = this.getWindowSize();
        const iMaxStart = Math.max(0, aKeys.length - iSize);

        const iRequested = Math.floor(toFiniteNumber(oTimelineModel.getProperty("/timelineWindowStart")));
        const iStart = iRequested < 0 ? iMaxStart : Math.min(iRequested, iMaxStart);

        const aVisible = aKeys.slice(iStart, iStart + iSize);
        const sFirst = aVisible.length ? oLabels[aVisible[0]] : "";
        const sLast = aVisible.length ? oLabels[aVisible[aVisible.length - 1]] : "";

        oTimelineModel.setProperty("/timelineWindowStart", iStart);
        oTimelineModel.setProperty("/timelineCanPrev", iStart > 0);
        oTimelineModel.setProperty("/timelineCanNext", iStart < iMaxStart);
        oTimelineModel.setProperty("/timelineWindowVisible", aKeys.length > iSize);
        oTimelineModel.setProperty("/timelineWindowText", aVisible.length
            ? `${sFirst} – ${sLast} (${iStart + 1}–${iStart + aVisible.length} of ${aKeys.length})`
            : "");

        return aVisible;
    }

    /** Moves the window by whole pages (negative = back in time). Re-renders from cached data. */
    public onTimelineWindowMove(iPages: number): void {
        const oTimelineModel = this.getModel("timeline");
        const iStart = Math.max(0, Math.floor(toFiniteNumber(oTimelineModel.getProperty("/timelineWindowStart"))));

        oTimelineModel.setProperty("/timelineWindowStart", Math.max(0, iStart + iPages * this.getWindowSize()));
        this.rerenderFromCache();
    }

    public onTimelineWindowPrev(): void {
        this.onTimelineWindowMove(-1);
    }

    public onTimelineWindowNext(): void {
        this.onTimelineWindowMove(1);
    }

    public onTimelineWindowFirst(): void {
        this.getModel("timeline").setProperty("/timelineWindowStart", 0);
        this.rerenderFromCache();
    }

    public onTimelineWindowLast(): void {
        this.resetColumnWindow();
        this.rerenderFromCache();
    }

    /** Handler for an optional window-size Select (e.g. 8 / 12 / 26 / 52); keeps the first visible period in view. */
    public onTimelineWindowSizeChange(): void {
        this.rerenderFromCache();
    }

    // =========================================================================
    // Timeline: fixating (drill-down)
    //
    // Used slots are always contiguous (1, then 2, then 3): new fixates go into the
    // first free slot and removing one shifts the later ones down.
    // =========================================================================

    private getFixate(iSlot: FixateSlot): Fixate {
        const oTimelineModel = this.getModel("timeline");
        return {
            value: oTimelineModel.getProperty(`/timelineYValue${iSlot}`) ?? "",
            dimension: oTimelineModel.getProperty(`/timelineYFixate${iSlot}`) ?? "",
        };
    }

    private writeFixate(iSlot: FixateSlot, oFixate: Fixate): void {
        const oTimelineModel = this.getModel("timeline");
        oTimelineModel.setProperty(`/timelineYValue${iSlot}`, oFixate.value);
        oTimelineModel.setProperty(`/timelineYFixate${iSlot}`, oFixate.dimension);
    }

    private getUsedFixates(): Fixate[] {
        return FIXATE_SLOTS.map((iSlot) => this.getFixate(iSlot)).filter((oFixate) => !!oFixate.dimension);
    }

    private areAllFixateSlotsUsed(): boolean {
        return this.getUsedFixates().length >= FIXATE_SLOTS.length;
    }

    private hasAnyFixate(sAxis: TimelineAxis): boolean {
        const oTimelineModel = this.getModel("timeline");
        return FIXATE_SLOTS.some((iSlot) => !!oTimelineModel.getProperty(`/timeline${sAxis}Fixate${iSlot}`));
    }

    /** A dimension is "in use" if it is the current Y item or already fixated. */
    private isDimensionInUse(sDimension: string): boolean {
        return this.getModel("timeline").getProperty("/timelineYItem") === sDimension
            || this.getUsedFixates().some((oFixate) => oFixate.dimension === sDimension);
    }

    private onTimelineParameterPress(oEvent: Link$PressEvent): void {
        const oLink = oEvent.getSource() as Link;
        const sValue = oLink.getBindingContext("timelineTable")?.getProperty("y") as string;

        // Destroy any previous popover so its links don't hold a stale value
        this._oFixatePopover?.destroy();

        const aDimensions = TIMELINE_DIMENSIONS.filter((sDimension) => !this.isDimensionInUse(sDimension));

        const oPopover = new Popover({
            showHeader: false,
            placement: PlacementType.Auto,
            content: [
                new VBox({
                    items: aDimensions.map((sDimension) => new Link({
                        text: sDimension,
                        press: () => {
                            oPopover.close();
                            this.fixateTimelineParameter(sValue, sDimension);
                        },
                    })),
                }).addStyleClass("sapUiSmallMargin"),
            ],
            afterClose: () => {
                // Use the local reference: by now this._oFixatePopover may already be a newer popover.
                oPopover.destroy();
                if (this._oFixatePopover === oPopover) {
                    this._oFixatePopover = undefined;
                }
            },
        });

        this._oFixatePopover = oPopover;
        oPopover.openBy(oLink);
    }

    private fixateTimelineParameter(sValue: string, sDimension: string): void {
        const aFixates = this.getUsedFixates();
        if (aFixates.length >= FIXATE_SLOTS.length) {
            aFixates.pop(); // all slots used: replace the last one
        }
        aFixates.push({ value: sValue, dimension: sDimension });

        this.writeFixates(aFixates);
    }

    public removeFixate(iSlot: FixateSlot): void {
        const aRemaining = FIXATE_SLOTS
            .filter((iOther) => iOther !== iSlot)
            .map((iOther) => this.getFixate(iOther))
            .filter((oFixate) => !!oFixate.dimension);

        this.writeFixates(aRemaining);
    }

    /** Writes the fixates into slots 1..n, clears the rest and reloads the timeline. */
    private writeFixates(aFixates: Fixate[]): void {
        FIXATE_SLOTS.forEach((iSlot, iIndex) => {
            this.writeFixate(iSlot, aFixates[iIndex] ?? { value: "", dimension: "" });
        });

        void this.applyTimelineConfiguration(false);
    }

    private async refreshSettingsList(sType: SettingsType): Promise<void> {
        const aSettings = await this.get(`${this._analyticsSettingsList}?type=${sType}`);
        const oModel = this.getView()?.getModel("timelineOptions") as JSONModel | undefined;
        if (oModel) {
            oModel.setProperty("/settings", aSettings);
        } else {
            this.setModel(new JSONModel({ settings: aSettings }), "timelineOptions");
        }
    }

    private confirmDeleteSetting(analyticsSettingId: number, onDeleted: () => Promise<void> | void): void {
        MessageBox.confirm(this.getView()?.getModel("i18n")?.getProperty("deleteConfirmText"), {
            title: this.getView()?.getModel("i18n")?.getProperty("deleteConfirmTitle"),
            actions: [MessageBox.Action.CANCEL, MessageBox.Action.DELETE],
            emphasizedAction: MessageBox.Action.DELETE,
            onClose: async (sAction: string | null) => {
                if (sAction !== MessageBox.Action.DELETE) {
                    return;
                }
                await this.get(this._analyticsDeleteTimelineSettings + "/" + analyticsSettingId);
                await onDeleted();
            }
        });
    }

    public async saveFilterSettings(): Promise<void> {
        const filtersModel = this.getModel("filters");
        const name: string = filtersModel.getProperty("/newSettingName");
        if (!name) return;

        const { ...settings } = filtersModel.getData();

        await this.post(this._analyticsSaveItemsSettings, {
            body: { newSettingName: name, settings }
        });

        filtersModel.setProperty("/newSettingName", "");
    }

    public async loadFilterSettings(analyticsSettingId: number): Promise<void> {
        this.closeDialog();

        const saved = await this.get(this._analyticsGetTimelineSettings + "/" + analyticsSettingId);
        if (!saved) return;

        const defaults = this.getDefaultFilters();
        this.getModel("filters").setData({
            ...defaults,
            ...saved,
            amount: { ...defaults.amount, ...saved.amount },
        });

        await this.applyFilters();
    }

    public deleteFilterSetting(analyticsSettingId: number): void {
        this.confirmDeleteSetting(analyticsSettingId, () => this.refreshSettingsList("ITEMS"));
    }

    public async saveTimelineSettings() {
        await this.post(this._analyticsSaveTimelineSettings, {
            body: this.getModel("timeline").getData()
        });

        await this.fetchModel(this._analyticsTimelineOptions, "timelineOptions");
        void this.applyTimelineConfiguration();
    }

    public async loadTimelineSettings(analyticsSettingId: number) {
        if (this._currentSettingsType === "ITEMS") {
            return this.loadFilterSettings(analyticsSettingId);
        }

        this.closeDialog();

        const timelineData = await this.get(this._analyticsGetTimelineSettings + "/" + analyticsSettingId);

        const xSub = timelineData.timelineXSubItems;
        const ySub = timelineData.timelineYSubItems;

        timelineData.timePeriodFrom = new Date(Date.parse(timelineData.timePeriodFrom));
        timelineData.timePeriodTo = new Date(Date.parse(timelineData.timePeriodTo));

        this.getModel("timeline").setData(timelineData);

        this.resetSubItems();
        this.resetColumnWindow();

        this.updatePeriodAvailability();

        await this.fetchModelPost(this._analyticsTimelineData, "timelineData", {
            body: this.buildTimelineRequest(),
        });

        this.getModel("timeline").setProperty("/timelineXSubItems", xSub);
        this.getModel("timeline").setProperty("/timelineYSubItems", ySub);

        await this.fetchModelPost(this._analyticsTimelineData, "timelineData", {
            body: this.buildTimelineRequest(),
        });

        const oGrouped = this.groupTimelineData(this.getTimelineContent());
        this.syncSubItemOptions(oGrouped);
        this.renderTimeline(oGrouped);
    }

    public deleteTimelineSetting(analyticsSettingId: number): void {
        if (this._currentSettingsType === "ITEMS") {
            this.deleteFilterSetting(analyticsSettingId);
            return;
        }

        this.confirmDeleteSetting(analyticsSettingId, async () => {
            await this.fetchModel(this._analyticsTimelineOptions, "timelineOptions");
            void this.applyTimelineConfiguration();
        });
    }

    public formatI18nText(sKey: string) {
        if (!sKey) {
            return "";
        }
        const oI18nModel = this.getOwnerComponent().getModel("i18n");
        return oI18nModel?.getProperty(sKey) || sKey;
    }

    public async openSettings(vTypeOrEvent?: SettingsType | Event) {
        this._currentSettingsType = vTypeOrEvent === "ITEMS" ? "ITEMS" : "TIMELINE";
        await this.refreshSettingsList(this._currentSettingsType);

        const settingsModel = new JSONModel({});

        this._settingsDialog = await this.openDialog(
            "akademika.akamont.pages.analytics.Settings",
            true
        );
        this.getView()?.addDependent(this._settingsDialog);
        this._settingsDialog.setModel(settingsModel, "settingsModel");
    }

    public async saveTemplateSettings() {
        this.closeDialog();

        const settings = this.getModel("timelineOptions").getProperty("/settings");
        await this.post(this._analyticsSaveTemplateSettings, {
            body: settings
        });
    }
}
